import { serverOptionsFilter } from "@dehub/theme";
import { Box, Select, Text } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import type { UseFormReturnType } from "@mantine/form";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useSwr, { mutate } from "swr";
import { NetworkApi, branchApi } from "../../apis";
import { useBusiness } from "../../pages/sales-invoice/new";
import { message } from "../../utils/Message";
import { renderBusinessName } from "../../utils/render-business-name";
import { renderName } from "../../utils/render-name";
import fieldClasses from "./invoice-form-fields.module.css";

type Props = { form: UseFormReturnType<any> };

function Kv({
  label,
  children,
  span2,
  mono,
}: {
  label: string;
  children: React.ReactNode;
  span2?: boolean;
  mono?: boolean;
}) {
  return (
    <Box className={`${fieldClasses.kv}${span2 ? ` ${fieldClasses.span2}` : ""}`}>
      <Text className={fieldClasses.label}>{label}</Text>
      {typeof children === "string" || typeof children === "number" ? (
        <Text className={mono ? fieldClasses.mono : fieldClasses.value}>{children}</Text>
      ) : (
        children
      )}
    </Box>
  );
}

export function InvoiceReceiverForm({ form }: Props) {
  const { t } = useTranslation("invoice");
  const [businessData, setBusinessData] = useState<any>();
  const [queryBusiness, setQueryBusiness] = useState<string | null>("");
  const [queryBranch, setQueryBranch] = useState<string | null>("");
  const [businessId, setBusinessId] = useState<string | null>("");
  const { getBusiness, isNew } = useBusiness();

  const { data: branchesList } = useSwr(
    businessId ? `/api/branch?businessId=${businessId}&query=${queryBranch}` : [],
    businessId ? async () => await branchApi.select({ query: queryBranch, businessId }) : null,
    {
      fallback: [],
    },
  );

  const { data: businessList = [] } = useSwr(
    `/api/network?&query=${queryBusiness ?? ""}`,
    async () => await NetworkApi.select({ query: queryBusiness ?? "" }),
    {
      fallback: [],
    },
  );

  const onSearchBranch = async (query: string | "", businessId: any) => {
    let timeout: any;
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(async () => {
      setBusinessId(businessId);
      setQueryBranch(query);
    }, 400);
  };

  const onSearchBusiness = async (query: string | null) => {
    let timeout: any;
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(async () => {
      setQueryBusiness(query);
    }, 400);
  };

  const getBusinessOnChange = async (id: string) => {
    let res = null;
    try {
      if (id) res = await NetworkApi.get(id);
      getBusiness(id);
      setBusinessData(res);
      form.setFieldValue("paymentDate", res?.paymentDate ?? null);

      setBusinessId(id);
      setQueryBranch("");

      mutate(`/api/branch?businessId=${id}&query=${queryBranch}`);
    } catch (error: any) {
      message.error(error?.message);
    }
  };

  useEffect(() => {
    if (form.values?.receiverBusinessId) {
      void getBusinessOnChange(form.values.receiverBusinessId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values?.receiverBusinessId]);

  useEffect(() => {
    if (isNew === true && branchesList?.length) {
      const headOfficeBranch = branchesList.find((item: { branch: { type: string } }) => item.branch.type === "HEAD_OFFICE");
      if (headOfficeBranch) {
        form.setFieldValue("receiverBranchId", headOfficeBranch.branch.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchesList, isNew]);

  const businessOptions =
    businessList?.map((item: any) => ({
      value: item.id,
      label: renderBusinessName(item, ""),
      refCode: item.refCode,
    })) ?? [];

  const branchOptions =
    branchesList && branchesList?.length > 0
      ? branchesList.map((item: any) => ({
          value: item?.branch?.id,
          label: `${item?.branch?.name ?? "-"}`,
          branchAddress: `${item?.branch?.branchAddress ?? ""}`,
        }))
      : [];

  const termDays = businessData?.paymentTerm?.configDay ?? businessData?.paymentTerm?.day;
  const paymentHint =
    termDays != null
      ? t("form.paymentTermAutoHint", { days: termDays })
      : t("form.paymentDueDateAuto");

  return (
    <Box>
      <Box className={fieldClasses.fields3}>
        <Select
          searchable
          clearable
          filter={serverOptionsFilter}
          onSearchChange={(e) => onSearchBusiness(e)}
          label={t("form.buyerBusinessName")}
          placeholder={t("form.buyerBusinessName")}
          data={businessOptions}
          renderOption={({ option }) => {
            const opt = businessOptions.find((o: any) => o.value === option.value);
            return (
              <Box>
                <Text size="sm">{option.label}</Text>
                <Text size="xs" c="dimmed">
                  #{(opt as any)?.refCode}
                </Text>
              </Box>
            );
          }}
          required
          {...form.getInputProps("receiverBusinessId")}
          onChange={(e: any) => {
            form.setFieldValue("receiverBusinessId", e);
            getBusinessOnChange(e);
            onSearchBranch("", e);
            form.setFieldValue("receiverBranchId", undefined);
            form.setFieldValue("paymentDate", undefined);
            form.setFieldValue("lines", []);
          }}
        />

        <Select
          searchable
          clearable
          disabled={!businessData}
          label={t("form.receiverAddress")}
          placeholder={t("form.receiverAddress")}
          onSearchChange={(e) =>
            onSearchBranch(
              e === "-" ? "" : e,
              form.values?.receiverBusinessId === "-" ? "" : form.values?.receiverBusinessId,
            )
          }
          data={branchOptions}
          renderOption={({ option }) => {
            const opt = branchOptions.find((o: any) => o.value === option.value);
            return (
              <Box>
                <Text size="sm">{option.label}</Text>
                <Text size="xs" c="dimmed">
                  {(opt as any)?.branchAddress}
                </Text>
              </Box>
            );
          }}
          required
          {...form.getInputProps("receiverBranchId")}
        />

        <Box>
          <DatePickerInput
            label={t("form.paymentDueDate")}
            placeholder={t("form.paymentDueDateAuto")}
            disabled={!businessData}
            valueFormat="YYYY-MM-DD"
            clearable={false}
            required
            {...form.getInputProps("paymentDate")}
          />
          <Text className={`${fieldClasses.label} ${fieldClasses.paymentHint}`}>{paymentHint}</Text>
        </Box>
      </Box>

      {businessData ? (
        <Box className={fieldClasses.kvPanel}>
          <Kv label={t("form.partnerName")}>
            {renderBusinessName(businessData?.partner) || "-"}
          </Kv>
          <Kv label={t("form.taxNumber")} mono>
            {businessData?.regNumber ?? "-"}
          </Kv>
          <Kv label={t("form.financeStaff")}>{renderName(businessData?.staff) || "-"}</Kv>
          <Kv label={t("form.paymentTerms")}>
            {businessData?.paymentTerm?.description ?? "-"}
          </Kv>
          <Kv label={t("form.businessAddress")} span2>
            {businessData?.partner?.businessAddress ?? "-"}
          </Kv>
          <Kv label={t("form.bankAccount")} span2>
            {businessData?.bankAccount ? (
              <Text className={fieldClasses.value} component="div">
                <Text span>{businessData.bankAccount?.name ?? "-"} · </Text>
                <Text span className={fieldClasses.mono}>
                  {businessData.bankAccount?.number ?? "-"}
                </Text>
              </Text>
            ) : (
              <Text c="red.8" fz="sm" fw={500}>
                {t("form.noBankAccount")}
              </Text>
            )}
          </Kv>
        </Box>
      ) : null}
    </Box>
  );
}
